import { createTheme, type Theme } from '@mui/material/styles';
import type { ThemeMode, ThemePalette } from './types';

// Warm "coach" accent — used sparingly for encouragement moments (hero
// gradients, coach cues, empty-state tints) so the clinical navy/teal palette
// keeps its authority while the product stops feeling cold.
declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['warning'];
  }
  interface PaletteOptions {
    accent?: PaletteOptions['warning'];
  }
}

// Refined academic/clinical design system:
//  - Primary: deep navy/slate blue — serious, trustworthy, academic.
//  - Secondary: muted dental teal / mint — the medical-adjacent accent.
//  - Backgrounds: warm off-white (light) / deep blue-slate (dark).
// Two selectable palettes, each with light and dark variants — students prep
// at night. Palette (color identity) and mode (light/dark) are independent.
const PALETTES = {
  navy: {
    light: {
      primary: { main: '#1f3a5f', dark: '#152a47', light: '#3d5a80', contrastText: '#ffffff' },
      secondary: { main: '#2a9d8f', dark: '#1f7a70', light: '#5bb8ad', contrastText: '#ffffff' },
    },
    dark: {
      primary: { main: '#8fb3d9', dark: '#6a94c2', light: '#b3cde6', contrastText: '#0e1826' },
      secondary: { main: '#5fc4b8', dark: '#3da99d', light: '#8ad8cf', contrastText: '#0e1826' },
    },
  },
slate: {
  light: {
    primary: { main: '#347C78', dark: '#285F5C', light: '#78B5B1', contrastText: '#ffffff' },
    secondary: { main: '#64748B', dark: '#475569', light: '#94A3B8', contrastText: '#ffffff' },
  },
  dark: {
    primary: { main: '#67C6BD', dark: '#40968E', light: '#9DE0DA', contrastText: '#0e1826' },
    secondary: { main: '#CBD5E1', dark: '#94A3B8', light: '#E2E8F0', contrastText: '#0e1826' },
  },
  },
} as const;

/** Swatch colors for the Settings theme picker. */
export function themePreviewColors(paletteName: ThemePalette): { primary: string; secondary: string } {
  const p = PALETTES[paletteName].light;
  return { primary: p.primary.main, secondary: p.secondary.main };
}

export function getTheme(mode: ThemeMode, paletteName: ThemePalette = 'navy'): Theme {
  const dark = mode === 'dark';
  const brand = PALETTES[paletteName] ?? PALETTES.navy;

  const palette = {
    mode,
    primary: dark ? brand.dark.primary : brand.light.primary,
    secondary: dark ? brand.dark.secondary : brand.light.secondary,
    accent: dark
      ? { main: '#e8a687', light: '#f2c4ae', dark: '#cd8666', contrastText: '#26160e' }
      : { main: '#c9704f', light: '#e39a7e', dark: '#a5563a', contrastText: '#ffffff' },
    success: { main: dark ? '#6fbf8f' : '#2e7d57' },
    warning: { main: dark ? '#e0a94f' : '#b26a00' },
    error: { main: dark ? '#e57368' : '#b3402f' },
    info: { main: dark ? '#7fb6d9' : '#356a8c' },
    background: dark
      ? { default: '#101720', paper: '#18212e' }
      : { default: '#f7f6f3', paper: '#ffffff' },
    text: dark
      ? { primary: '#e6ebf1', secondary: '#9aa8b8' }
      : { primary: '#1c2733', secondary: '#5a6b7d' },
    divider: dark ? 'rgba(154, 168, 184, 0.16)' : '#e5e3dd',
  } as const;

  const cardBorder = dark ? 'rgba(154, 168, 184, 0.18)' : '#e7e5df';
  const cardShadow = dark
    ? '0 1px 3px rgba(0, 0, 0, 0.4)'
    : '0 1px 2px rgba(28, 39, 51, 0.05), 0 2px 8px rgba(28, 39, 51, 0.04)';

  return createTheme({
    palette,
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
      h4: { fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.75rem' },
      h5: { fontWeight: 700, letterSpacing: '-0.015em' },
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      overline: { fontWeight: 600, letterSpacing: '0.08em' },
      button: { fontWeight: 600, textTransform: 'none' as const, letterSpacing: 0 },
      body2: { lineHeight: 1.6 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Guard against any stray wide element causing sideways scroll on phones.
          body: { overflowX: 'hidden' },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            borderColor: cardBorder,
            boxShadow: cardShadow,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCardContent: {
        styleOverrides: {
          root: { '&:last-child': { paddingBottom: 16 } },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 10,
            // Comfortable touch targets without inflating dense toolbars.
            minHeight: 36,
          },
          sizeLarge: { minHeight: 46 },
          sizeSmall: { minHeight: 32 },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 10 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiTextField: {
        defaultProps: { variant: 'outlined' },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: dark ? 'rgba(255,255,255,0.02)' : '#fff',
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: dark ? 'rgba(154,168,184,0.16)' : '#ebe9e3',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
            backgroundImage: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            // Dialogs must fit phone screens: tighter margins, near-full width.
            [theme.breakpoints.down('sm')]: {
              margin: 12,
              width: 'calc(100% - 24px)',
              maxHeight: 'calc(100% - 24px)',
            },
          }),
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { fontSize: 12, borderRadius: 8 },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: { fontWeight: 600 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
    },
  });
}

/** @deprecated Use getTheme(mode); kept for anything importing the old constant. */
export const theme = getTheme('light');
